/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
// import Async from "react-select/async"
import { AsyncPaginate } from "react-select-async-paginate"
import FormWrap from "../FormWrap"
import { ValidationRule, FieldErrors } from "react-hook-form"
import { selectStyle } from "./style"

export interface iSelectProps {
  name: string
  initialValue?: any
  value?: any
  defaultOptions?: Array<any> | boolean
  isClearable?: boolean
  isSearchable?: boolean
  isMulti?: boolean
  placeholder?: string
  label?: string
  error?: FieldErrors
  validation?: ValidationRule
  loadOptions: (input: string, skip: number) => Promise<any>
  onChange?: (prop: string | string[]) => void
}

const AsyncPaginateSelectInput: FunctionComponent<iSelectProps> = ({
  defaultOptions,
  isClearable,
  isSearchable,
  isMulti = false,
  placeholder,
  name,
  error,
  onChange,
  loadOptions,
  label,
  value,
}) => {
  const [_data, setData] = useState<any[]>(
    Array.isArray(defaultOptions) ? defaultOptions : [],
  )
  const [_value, setValue] = useState<any>(_data.find((x) => x.value === value))
  const [isLoading, setLoading] = useState(true)
  const _input = useRef("")
  const skip = useRef(0)

  const style = useMemo(() => selectStyle({ isMulti, error }), [isMulti])

  const onInputChange = useCallback(
    (data: any) => {
      setValue(data)
      if (Array.isArray(data)) {
        onChange && onChange(data ? data.map((d) => d.value) : "")
      } else {
        onChange && onChange(data ? data.value : "")
      }
      skip.current = 0
    },
    [name],
  )

  useEffect(() => {
    if (Array.isArray(_data) && value) {
      if (isMulti) {
        const array = []
        for (let i = 0; i < value.length; i++) {
          const x = _data.find((d) => d.value === value[i])
          if (x) array.push(x)
        }
        setValue(array)
      } else {
        const x = _data.find((d) => d.value === value)
        setValue(x)
      }
    }
  }, [value, _data])

  const onLoadOptions = async (input: string) => {
    if (input !== _input.current) skip.current = 0
    _input.current = input
    setLoading(true)
    const result = await loadOptions(input, skip.current)
    skip.current += 15

    if (!_data.length) {
      setData(result.data)
    }
    setLoading(false)
    return { options: result.data || [], hasMore: skip.current < result.total }
  }

  return (
    <div className="form-group">
      <label htmlFor={name}>
        {label}
        {error && <span>{error.message}</span>}
      </label>
      <div className="form-control">
        <AsyncPaginate
          id={name}
          cacheOptions
          defaultOptions={defaultOptions}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          value={_value}
          isLoading={isLoading}
          placeholder={placeholder}
          loadOptions={onLoadOptions}
          classNamePrefix={"react-select"}
          menuPlacement="auto"
          className={"select"}
          styles={style}
          onChange={onInputChange}
        />
      </div>
    </div>
  )
}

const MemoSelectInput = memo(
  AsyncPaginateSelectInput,
  (currentProps, nextProps) => {
    const { initialValue } = currentProps
    const { initialValue: nextInitial } = nextProps

    return (
      initialValue === nextInitial &&
      currentProps.error?.message === nextProps.error?.message &&
      currentProps.defaultOptions === nextProps.defaultOptions
    )
  },
)

// export default MemoSelectInput
export default AsyncPaginateSelectInput

export const FormAsyncSelectInput = FormWrap<iSelectProps>(MemoSelectInput)
